<template>
  <div>
    <div class="p-fluid p-formgrid p-grid">
      <div class="p-field p-col-12 p-md-6">
        <label for="pseProjQ">Project name or town</label>
        <InputText id="pseProjQ" v-model.trim="q" placeholder="Type 3+ letters, e.g. CROWBOROUGH" @keyup.enter="search" />
      </div>
      <div class="p-field p-col-6 p-md-3">
        <label for="pseProjArea">Postcode area (e.g. DN)</label>
        <InputText id="pseProjArea" v-model.trim="area" placeholder="e.g. DN" style="text-transform: uppercase;" @keyup.enter="search" />
      </div>
      <div class="p-field p-col-6 p-md-3" style="display: flex; align-items: flex-end; gap: 8px;">
        <Button label="Search" icon="pi pi-search" @click="search" :loading="loading" />
        <Button v-if="allowNew" label="New project" icon="pi pi-plus" class="p-button-warning" @click="openNew" />
      </div>
    </div>

    <div v-if="error" class="pse-note error"><i class="pi pi-times-circle"></i><span>{{ error }}</span></div>
    <div v-if="searched && !loading && !results.length" class="pse-note info">
      <i class="pi pi-info-circle"></i><span>No project matches. <span v-if="allowNew" class="pse-link" @click="openNew">Create a new project</span></span>
    </div>

    <div v-for="p in results" :key="p.projectId" class="pse-card pse-project">
      <div class="pse-row">
        <strong>{{ p.name }}</strong>
        <span class="pse-muted">{{ [p.postcodeArea, p.postCode, p.town].filter(Boolean).join(' · ') }}</span>
        <span v-if="p.legacy" class="pse-chip legacy">{{ p.postcodeArea ? 'from an old enquiry' : 'old enquiry, no postcode area' }}</span>
        <span class="pse-spacer"></span>
        <Button v-if="pickProject" label="Use this project" class="p-button-sm" @click="$emit('project', p)" />
      </div>
      <div style="margin-top: 6px;">
        <span class="pse-muted">Phases: </span>
        <span v-if="!p.phases || !p.phases.length" class="pse-muted">none (main project only)</span>
        <span v-for="ph in p.phases" :key="ph.phaseId" class="pse-chip phase">Phase {{ ph.phaseNo }}<span v-if="ph.description">: {{ ph.description }}</span></span>
      </div>
      <table v-if="p.sharedEnquiries && p.sharedEnquiries.length" class="pse-table" style="margin-top: 8px;">
        <thead>
          <tr><th>Enquiry</th><th>Phase</th><th>Quotations already on it</th><th></th></tr>
        </thead>
        <tbody>
          <tr v-for="s in p.sharedEnquiries" :key="s.sharedEnqId">
            <td>{{ s.description || ('Shared enquiry ' + s.sharedEnqId) }}</td>
            <td>{{ phaseLabel(p.phases, s.phaseId) }}</td>
            <td>
              <div v-for="b in s.bids" :key="b.oppId">
                {{ b.custName }} <span class="pse-muted">· {{ b.productType }} · {{ b.branch }} · {{ b.sQuoteNo || 'no quotation yet' }}</span>
              </div>
              <span v-if="!s.bids || !s.bids.length" class="pse-muted">none yet</span>
            </td>
            <td style="white-space: nowrap;">
              <Button :label="enquiryAction" class="p-button-sm p-button-outlined" @click="$emit('sharedEnquiry', { project: p, sharedEnq: s })" />
            </td>
          </tr>
        </tbody>
      </table>
      <div v-else class="pse-muted" style="margin-top: 6px;">No enquiries on this project yet.</div>
    </div>

    <Dialog header="New project" :visible="newVisible" :modal="true" :style="{ width: '560px' }" @update:visible="newVisible = $event">
      <div class="p-fluid">
        <div class="p-field">
          <label for="pseNpName">Project name <span class="pse-req">*</span></label>
          <InputText id="pseNpName" v-model.trim="np.name" style="text-transform: uppercase;" />
        </div>
        <div class="p-formgrid p-grid">
          <div class="p-field p-col-6">
            <label for="pseNpArea">Postcode area (e.g. DN) <span class="pse-req">*</span></label>
            <AutoComplete id="pseNpArea" v-model="np.area" :suggestions="areaSuggestions" field="label" placeholder="Type the letters, e.g. DN"
              :dropdown="false" @complete="findAreas($event.query)" />
            <small class="pse-muted">The letters of the postcode (DN for Doncaster). A district or full postcode (DN4, DN4 5HZ) is accepted and stored as its area.</small>
          </div>
          <div class="p-field p-col-6">
            <label for="pseNpPc">Full postcode (optional)</label>
            <InputText id="pseNpPc" v-model.trim="np.postCode" style="text-transform: uppercase;" />
          </div>
        </div>
        <div class="p-field">
          <label for="pseNpA1">Street</label>
          <InputText id="pseNpA1" v-model.trim="np.address1" />
        </div>
        <div class="p-formgrid p-grid">
          <div class="p-field p-col-6">
            <label for="pseNpA2">Sub area</label>
            <InputText id="pseNpA2" v-model.trim="np.address2" />
          </div>
          <div class="p-field p-col-6">
            <label for="pseNpTown">Town</label>
            <InputText id="pseNpTown" v-model.trim="np.town" />
          </div>
        </div>
        <div v-if="newError" class="pse-note error"><i class="pi pi-times-circle"></i><span>{{ newError }}</span></div>
      </div>
      <template #footer>
        <Button label="Cancel" icon="pi pi-times" class="p-button-text" @click="newVisible = false" />
        <Button label="Create project" icon="pi pi-check" :loading="saving" @click="createProject" />
      </template>
    </Dialog>
  </div>
</template>

<script>
import api from './sharedEnquiryApi'
import { phaseLabel, errorText, TYPE_AHEAD_MS } from './sharedEnquiryLogic'

const emptyProject = () => ({ name: '', area: null, postCode: '', address1: '', address2: '', town: '' })

export default {
  name: 'ProjectPicker',
  props: {
    allowNew: { type: Boolean, default: true },
    pickProject: { type: Boolean, default: true },
    enquiryAction: { type: String, default: 'Add a customer to this enquiry' }
  },
  data() {
    return {
      q: '', area: '', results: [], loading: false, searched: false, error: '', timer: null, seq: 0,
      newVisible: false, np: emptyProject(), areaSuggestions: [], newError: '', saving: false
    }
  },
  watch: {
    q() { this.typed() },
    area() { this.typed() }
  },
  beforeDestroy() {
    clearTimeout(this.timer)
  },
  methods: {
    phaseLabel,
    enough() {
      return (this.q || '').length >= 3 || !!(this.area || '').trim()
    },
    typed() {
      clearTimeout(this.timer)
      if (!this.enough()) {
        // too short to search: clear, but no error while the user is still typing
        this.seq++
        this.results = []
        this.searched = false
        this.loading = false
        this.error = ''
        return
      }
      this.timer = setTimeout(() => this.search(), TYPE_AHEAD_MS)
    },
    // Never an empty search: that is every project (one per old enquiry) with all its bids.
    async search() {
      clearTimeout(this.timer)
      if (!this.enough()) {
        this.error = 'Type at least 3 letters of the project name or town, or a postcode area.'
        this.results = []
        this.searched = false
        return
      }
      // only the latest search's answer is shown: an earlier, slower one must not overwrite it
      const seq = ++this.seq
      this.loading = true
      this.error = ''
      try {
        const rows = await api.projects(this.q, (this.area || '').toUpperCase())
        if (seq === this.seq) this.results = rows || []
      } catch (e) {
        if (seq === this.seq) {
          this.error = errorText(e, 'Could not search projects')
          this.results = []
        }
      } finally {
        if (seq === this.seq) {
          this.loading = false
          this.searched = true
        }
      }
    },
    openNew() {
      this.np = emptyProject()
      this.np.name = (this.q || '').toUpperCase()
      this.newError = ''
      this.newVisible = true
    },
    async findAreas(query) {
      try {
        const rows = await api.postcodeAreas((query || '').toUpperCase())
        this.areaSuggestions = rows.map(a => ({ ...a, label: a.code + (a.town ? ' (' + a.town + ')' : '') }))
      } catch (e) {
        this.areaSuggestions = []
      }
    },
    areaCode() {
      const a = this.np.area
      if (!a) return ''
      return (typeof a === 'string' ? a : a.code).trim().toUpperCase()
    },
    async createProject() {
      this.newError = ''
      const missing = []
      if (!this.np.name) missing.push('Project name')
      if (!this.areaCode()) missing.push('Postcode area (e.g. DN)')
      if (missing.length) {
        this.newError = 'Please fill in: ' + missing.join(', ') + '.'
        return
      }
      this.saving = true
      try {
        const p = await api.createProject({
          name: this.np.name.toUpperCase(), postcodeArea: this.areaCode(), postCode: (this.np.postCode || '').toUpperCase() || undefined,
          address1: this.np.address1 || undefined, address2: this.np.address2 || undefined,
          town: this.np.town || (typeof this.np.area === 'object' && this.np.area ? this.np.area.town : undefined)
        })
        this.newVisible = false
        this.$emit('project', { phases: [], sharedEnquiries: [], ...p })
      } catch (e) {
        this.newError = errorText(e, 'Could not create the project')
      } finally {
        this.saving = false
      }
    }
  }
}
</script>

<style scoped>
@import './sharedEnquiry.css';
</style>
